import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/utils/api";
import { Zap, Eye, EyeOff, Phone, Shield, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const DEMO_ACCOUNTS = [
  { role: "INOVIT Admin", mobile: "9000000001", password: "123456", color: "bg-orange-500" },
  { role: "Admin", mobile: "9999999999", password: "admin123", color: "bg-red-500" },
  { role: "PH-Kashi", mobile: "7777777777", password: "admin123", color: "bg-purple-500" },
  { role: "CI-VNS", mobile: "8888888888", password: "admin123", color: "bg-blue-500" },
  { role: "CI-PRG", mobile: "3333333333", password: "admin123", color: "bg-cyan-500" },
  { role: "CI-ALG", mobile: "1111111111", password: "admin123", color: "bg-teal-500" },
  { role: "Supervisor", mobile: "6666666666", password: "admin123", color: "bg-indigo-500" },
  { role: "Technician", mobile: "5555555555", password: "admin123", color: "bg-green-500" },
  { role: "Driver", mobile: "4444444444", password: "admin123", color: "bg-yellow-600" },
  { role: "Fleet Incharge", mobile: "9876543210", password: "admin123", color: "bg-amber-500" },
  { role: "Surveyor", mobile: "9988776655", password: "admin123", color: "bg-lime-600" },
  { role: "Warehouse Mgr", mobile: "9871234567", password: "admin123", color: "bg-emerald-500" },
  { role: "Finance", mobile: "2222222222", password: "admin123", color: "bg-pink-500" },
  { role: "Inv. Auditor", mobile: "8001234567", password: "admin123", color: "bg-rose-500" },
  { role: "Safety Auditor", mobile: "8002345678", password: "admin123", color: "bg-fuchsia-500" },
  { role: "Process Auditor", mobile: "8003456789", password: "admin123", color: "bg-violet-500" },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("login"); // login | otp
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post("/auth/seed");
      toast.success(`Data seeded: ${res.data.counts?.users || 0} users created`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Already seeded or error";
      toast.info(msg);
    } finally {
      setSeeding(false);
    }
  };

  const handleSendOTP = async () => {
    if (!mobile || mobile.length < 10) { toast.error("Enter valid mobile number"); return; }
    setLoading(true);
    try {
      await api.post("/auth/send-otp", { mobile });
      toast.success("OTP sent! (Demo OTP: 1234)");
      setStep("otp");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error sending OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) { toast.error("Enter OTP"); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/verify-otp", { mobile, otp });
      login(res.data.token, res.data.user);
      toast.success(`Welcome ${res.data.user.full_name || "User"}!`);
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (mob, pw) => {
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { mobile: mob || mobile, password: pw || password });
      login(res.data.token, res.data.user);
      toast.success(`Welcome ${res.data.user.full_name || "User"}!`);
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Left: Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 bg-white max-w-lg">
        {/* Brand */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-6">
            <img src="/images/logo.png" alt="INOVIT" className="h-10 w-auto" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            {step === "otp" ? "Enter OTP" : "Sign In"}
          </h1>
          <p className="text-slate-500 text-sm">
            {step === "otp" ? `OTP sent to ${mobile}` : "Access the INOVIT DT Installation Management System"}
          </p>
        </div>

        {step === "login" ? (
          <div className="space-y-4">
            <div>
              <label className="field-label">Mobile Number</label>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-3.5 text-slate-400" />
                <input
                  type="tel"
                  className="field-input pl-9"
                  placeholder="10-digit mobile number"
                  value={mobile}
                  onChange={e => setMobile(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSendOTP()}
                  data-testid="mobile-input"
                />
              </div>
            </div>
            <div>
              <label className="field-label">Password</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  className="field-input pr-10"
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handlePasswordLogin()}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-3 text-slate-400"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              onClick={() => handlePasswordLogin()}
              disabled={loading}
              className="w-full h-11 bg-orange-500 text-white font-bold rounded-sm hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 text-sm uppercase tracking-wider disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : (<>Sign In <ArrowRight size={16} /></>)}
            </button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative text-center"><span className="bg-white px-3 text-xs text-slate-400">OR LOGIN WITH OTP</span></div>
            </div>
            <button
              onClick={handleSendOTP}
              disabled={loading}
              className="w-full h-11 border-2 border-slate-200 text-slate-700 font-semibold rounded-sm hover:border-orange-400 hover:text-orange-600 transition-colors text-sm"
              data-testid="send-otp-btn"
            >
              Send OTP to Mobile
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="field-label">OTP (Demo: 1234)</label>
              <input
                type="text"
                className="field-input text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="1234"
                maxLength={6}
                value={otp}
                onChange={e => setOtp(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleVerifyOTP()}
                autoFocus
                data-testid="otp-input"
              />
            </div>
            <button
              onClick={handleVerifyOTP}
              disabled={loading}
              className="w-full h-11 bg-orange-500 text-white font-bold rounded-sm hover:bg-orange-600 transition-colors text-sm uppercase tracking-wider disabled:opacity-60"
              data-testid="verify-otp-btn"
            >
              {loading ? "Verifying..." : "Verify & Sign In"}
            </button>
            <button
              onClick={() => setStep("login")}
              className="w-full text-sm text-slate-500 hover:text-slate-700"
            >
              Back to Login
            </button>
          </div>
        )}

        {/* Demo seed button */}
        <div className="mt-8 p-4 bg-slate-50 rounded-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={14} className="text-orange-500" />
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Demo Setup</span>
          </div>
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="w-full text-xs text-orange-600 hover:text-orange-700 font-medium py-1.5 border border-orange-200 rounded-sm hover:bg-orange-50 transition-colors"
            data-testid="seed-data-btn"
          >
            {seeding ? "Seeding..." : "Load Demo Data (First Time Only)"}
          </button>
          <div className="mt-3 space-y-1">
            <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mb-1">Quick Login:</div>
            {DEMO_ACCOUNTS.map(acc => (
              <button
                key={acc.mobile}
                onClick={() => handlePasswordLogin(acc.mobile, acc.password)}
                className="w-full text-left text-xs px-2 py-1 rounded hover:bg-slate-100 flex items-center gap-2"
                data-testid={`quick-login-${acc.role.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className={`w-2 h-2 rounded-full ${acc.color}`}></span>
                <span className="font-medium text-slate-700">{acc.role}</span>
                <span className="text-slate-400 ml-auto font-mono">{acc.mobile}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Background Image */}
      <div
        className="hidden lg:flex flex-1 relative overflow-hidden"
        style={{
          backgroundImage: `url("https://images.unsplash.com/photo-1587407505264-fbf135123ae1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200")`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="text-4xl font-black mb-4 leading-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            Smart Meter<br />Installation ERP
          </div>
          <p className="text-slate-300 text-base mb-8 max-w-sm">
            Managing 15 circles, 600+ field teams, 150+ DT installations per day across India's electrical grid.
          </p>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Circles", value: "15" },
              { label: "DTs/Day", value: "150+" },
              { label: "Employees", value: "600+" },
            ].map(stat => (
              <div key={stat.label} className="text-center p-3 bg-white/10 rounded-sm backdrop-blur-sm">
                <div className="text-2xl font-black text-orange-400">{stat.value}</div>
                <div className="text-xs text-slate-300 uppercase tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
